import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArticleOutlined as ArticleOutlinedIcon,
  EditNoteOutlined as EditNoteOutlinedIcon,
  EngineeringOutlined as EngineeringOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  DoneAllOutlined as DoneAllOutlinedIcon,
  HighlightOffRounded as HighlightOffRoundedIcon,
  RestaurantMenuOutlined as RestaurantMenuOutlinedIcon,
  Search as SearchOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  MoreVert as MoreVertIcon,
} from "../../../components/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader";
import { useCateringStore } from "../../../store/useCateringStore";
import { getCateringHistory } from "../../../services/dashboard";
import { useToast } from "../../../hooks/useToast";
import History from "../claims/common/History";
import {
  OperationsEmptyState,
  OperationsPageShell,
  ifasColors,
} from "../ifas/components/OperationsUI";
import {
  StandardCountBadge,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  Tone,
  standardGhostBtnSx,
  standardOutlineBtnSx,
  standardTableHeadCellSx,
  standardTableRowSx,
} from "../ifas/components/StandardUI";

// Catering lifecycle -> tone, matching the system-wide status vocabulary
// (CateringStatus.java: DRAFT / IN_PROGRESS / CONFIRMED / IN_PREPARATION /
// DISPATCHED / ON_SITE / COMPLETED / CANCELLED).
const CATERING_STATUS_TONE: Record<string, Tone> = {
  DRAFT: "amber",
  IN_PROGRESS: "blue",
  CONFIRMED: "purple",
  IN_PREPARATION: "blue",
  DISPATCHED: "blue",
  ON_SITE: "blue",
  COMPLETED: "green",
  CANCELLED: "red",
};

const STATUS_OPTIONS = [
  "DRAFT",
  "IN_PROGRESS",
  "CONFIRMED",
  "IN_PREPARATION",
  "DISPATCHED",
  "ON_SITE",
  "COMPLETED",
  "CANCELLED",
];

const humanizeStatus = (value?: string) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (date?: string) =>
  date ? dayjs(date).format("YYYY-MM-DD") : "N/A";

const getDisplayValue = (value?: string | null) => value || "N/A";

export default function CateringPortal({
  fetchCatering,
  currentPage,
  rowsPerPage,
  setCurrentPage,
  appliedSearchText,
  selectedStatus,
  onSelectedCatering,
}: any) {
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActionItem, setSelectedActionItem] = useState<any>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  // Seeded from the dashboard-level props (previously destructured and
  // discarded), then owned locally so the banner toolbar below is live.
  const [searchText, setSearchText] = useState(appliedSearchText || "");
  const [statusFilter, setStatusFilter] = useState(
    STATUS_OPTIONS.includes(String(selectedStatus).toUpperCase())
      ? String(selectedStatus).toUpperCase()
      : "",
  );
  const { catering, totalCount, statusCounts } = useCateringStore();
  const { showInfoToast } = useToast();

  const fetchAllCatering = async (page = 0, size = 10) => {
    setIsLoading(true);
    const response = await fetchCatering(page, size);

    if (response?.status === 200) {
      useCateringStore.getState().setCatering(response.data);
    } else {
      showInfoToast(response?.data?.message || "Failed to load catering requests");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (fetchCatering) {
      fetchAllCatering(currentPage, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCatering, currentPage, rowsPerPage]);

  const handleHistoryClick = async () => {
    if (!selectedActionItem?.cateringId) {
      return;
    }

    setAnchorEl(null);
    setIsLoading(true);
    const res = await getCateringHistory(selectedActionItem.cateringId);
    if (res?.status === 200) {
      setHistoryData(res.data);
      setShowHistory(true);
    } else {
      showInfoToast("Failed to load catering history. Please try again.");
    }
    setIsLoading(false);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const handleOpenDetails = (row: any) => {
    useCateringStore.getState().setSelectedCatering(row);
    onSelectedCatering(row.cateringId);
  };

  // Real search/status filtering over the currently loaded page — the portal
  // received these values as props and never used them.
  const visibleCatering = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return catering.filter((row: any) => {
      if (statusFilter && String(row.status).toUpperCase() !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        row.cateringNumber,
        row.iceClaimNumber,
        row.funeralFileNumber,
        row.deceasedFullName,
        row.claimantContactNumber,
      ]
        .map((v: any) => String(v || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [catering, searchText, statusFilter]);

  if (showHistory) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <History
          onBack={handleBackFromHistory}
          historyData={historyData}
          isLoading={isLoading}
          historyType="case"
        />
      </Box>
    );
  }

  // KPI buckets derived exhaustively from CateringStatus.java — Total plus
  // one card per real status (Fulfilment rolls up IN_PREPARATION + DISPATCHED
  // + ON_SITE), summing to Total. No invented PLANNED status.
  const metrics = [
    {
      label: "Total",
      value: totalCount || 0,
      caption: "All catering requests",
      tone: "orange" as Tone,
      icon: <ArticleOutlinedIcon />,
    },
    {
      label: "Draft",
      value: statusCounts?.DRAFT || 0,
      caption: "Being captured",
      tone: "amber" as Tone,
      icon: <EditNoteOutlinedIcon />,
    },
    {
      label: "In Progress",
      value: statusCounts?.IN_PROGRESS || 0,
      caption: "Menu & guests in motion",
      tone: "blue" as Tone,
      icon: <EngineeringOutlinedIcon />,
    },
    {
      label: "Confirmed",
      value: statusCounts?.CONFIRMED || 0,
      caption: "Locked for service",
      tone: "purple" as Tone,
      icon: <CheckCircleOutlineIcon />,
    },
    {
      label: "Fulfilment",
      value:
        (statusCounts?.IN_PREPARATION || 0) +
        (statusCounts?.DISPATCHED || 0) +
        (statusCounts?.ON_SITE || 0),
      caption: "Preparing, dispatched or on site",
      tone: "blue" as Tone,
      icon: <LocalShippingOutlinedIcon />,
    },
    {
      label: "Completed",
      value: statusCounts?.COMPLETED || 0,
      caption: "Served and closed",
      tone: "green" as Tone,
      icon: <DoneAllOutlinedIcon />,
    },
    {
      label: "Cancelled",
      value: statusCounts?.CANCELLED || 0,
      caption: "Withdrawn requests",
      tone: "red" as Tone,
      icon: <HighlightOffRoundedIcon />,
    },
  ];

  return (
    <OperationsPageShell
      eyebrow="Hospitality & Service"
      title="Catering Portal"
      description="Plan catering requests with a clean view of guest service, funeral dates, contact details, and fulfilment progress."
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={() => fetchAllCatering(currentPage, rowsPerPage)}
          sx={standardGhostBtnSx}
        >
          Refresh
        </Button>
      }
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            lg: "repeat(7, minmax(0, 1fr))",
          },
          gap: 1.75,
          mb: 2.5,
        }}
      >
        {metrics.map((metric) => (
          <StandardMetricCard
            key={metric.label}
            label={metric.label}
            value={metric.value}
            caption={metric.caption}
            tone={metric.tone}
            icon={metric.icon}
            compact
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          mb: 2,
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: ifasColors.softOrange,
                color: ifasColors.orange,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <RestaurantMenuOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                Recent catering
              </Typography>
              <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}>
                Search by deceased, funeral file or catering number, or filter to a single status.
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              placeholder="Search deceased, funeral file, catering number"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
              sx={{ minWidth: 280, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              size="small"
              select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {humanizeStatus(option)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: ifasColors.surface,
          boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        }}
      >
        <Box sx={{ px: 2.5, py: 2.25, borderBottom: `1px solid ${ifasColors.border}`, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>Recent Catering</Typography>
          <StandardCountBadge count={totalCount || 0} />
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Catering</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral File</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Deceased</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral Date</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Menu</TableCell>
                <TableCell align="right" sx={standardTableHeadCellSx}>Guests</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Contact</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                <TableCell align="right" sx={standardTableHeadCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCatering.map((row: any) => {
                const status = String(row.status || "").toUpperCase();

                return (
                  <TableRow
                    key={row.cateringId}
                    hover
                    sx={standardTableRowSx}
                    onClick={() => handleOpenDetails(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography fontWeight={900} color={ifasColors.text}>
                        {row.cateringNumber || getDisplayValue(row.iceClaimNumber)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.iceClaimNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {row.funeralFileNumber || "Not linked"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {getDisplayValue(row.deceasedFullName)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {formatDate(row.funeralDate)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {getDisplayValue(row.cateringSetup?.menuCategoryName || row.menu?.menuCategoryDisplayName)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {row.cateringSetup?.guestCount ?? "N/A"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {getDisplayValue(row.claimantContactNumber)}
                    </TableCell>
                    <TableCell>
                      <StatusDotChip status={status} tone={CATERING_STATUS_TONE[status] || "grey"} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        <Button
                          variant="outlined"
                          sx={{ ...standardOutlineBtnSx, minHeight: 34, px: 1.6 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDetails(row);
                          }}
                        >
                          Open
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedActionItem(row);
                            setAnchorEl(event.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {visibleCatering.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <OperationsEmptyState
                      title="No catering records found"
                      description={
                        catering.length === 0
                          ? "Catering requests will appear here once they are captured."
                          : "No catering request matches the current search or status filter."
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedActionItem(null);
          }}
        >
          <MenuItem onClick={handleHistoryClick}>History</MenuItem>
        </Menu>

        <StandardPager
          page={currentPage}
          total={totalCount || 0}
          pageSize={rowsPerPage}
          onPage={setCurrentPage}
        />
      </Paper>
    </OperationsPageShell>
  );
}
